import { z } from "../zod.js";
import { Locale } from "../locales.js";

// Module 07 — Quality domain events (spec 07 §6). The typed catalog of the
// events this module EMITS today, plus the (deliberately empty) set it
// consumes over the relay.
//
// THIS FILE CLOSES A NAMED GAP (07-B-2 decision D-135, the D12 ruling).
// `apps/api/src/modules/quality/quality-events.ts` shipped 07-A's four
// emitters with local TypeScript interfaces and said so itself: "There is no
// `events/quality.ts` … the contracts catalog plus its parity arm are owed
// work." 07-B-2 is the first slice to add events since that sentence was
// written, and an event catalog that lands after three more events is a
// bigger diff with three more chances to drift. Every payload here is
// `.parse()`d at its emit site, and `qualityProducerEventNames` is held
// against the emitter's string literals BOTH WAYS by
// `test/unit/module-catalog-parity.test.ts` — the shape every other module's
// catalog already has.
//
// TEN REGISTERED EVENTS: 07-A's four (`inspection_created`,
// `inspection_passed`, `inspection_failed`, `control_point_versioned`),
// 07-B-1's `hold_placed`, 07-B-2's two (`hold_released`, `hold_disposed`),
// 07-C-2's `cut_completed` and 07-D-4b's two receiving rows
// (`receiving_failed`, `receiving_line_dispositioned`). *(This paragraph read
// "SEVEN EVENTS" and listed `cut_completed` as still-absent until 2026-08-22;
// 07-C-2 registered it with its emitter as the block above that payload says
// it would, and the count sentence was the line nobody re-read. Corrected
// then rather than left to mislead the next reader who counts — and updated
// again HERE, in the same commit that moves the count 8 → 10, for the same
// reason.)*
//
// §6's remaining TWO rows still name a table no shipped slice writes —
// `coa_issued`/`coa_revoked` need `qc_coa_documents` (07-E). An emitter for an
// event no code path can raise is the same defect as a route that 501s (D-026,
// D-033), so they join WITH their emitters, exactly as the receiving pair
// below did: 07-D-2 adjudicated the two payloads and deliberately left both
// names out of the map; 07-D-4b ships `QualityEvents.receivingFailed` /
// `receivingLineDispositioned` and registers them in the same change.
//
// NO CONSUMER IN THIS DEPLOYMENT, STATED RATHER THAN IMPLIED. §6's consumer
// column names modules 05/06/09/10/12 and module 04's PROJECTION half (D-123
// ruling 4 withdrew the gate half); of those only 04/05/06 exist, and every
// synchronous effect a §6 row describes — the quarantine flip, the gate
// clear, the ledger posting — already happened in THIS module's transaction
// through the owning module's in-txn seam (D-065). Nothing subscribes today;
// emission is pinned in the integration suite, which can read the outbox — a
// walkthrough speaks HTTP and cannot (spec 06 §10's rule). That is the same
// position module 05's six ledger events shipped in.
//
// §6's shared envelope fields (tenant, actor, occurred_at, event id) are
// added by `OutboxService.append`; each payload below carries only its own.

const Uuid = z.guid();
const MinorMoney = z.number().int();
const Currency = z.string().length(3);

/** §9.10 + D-128: the money pair rides TOGETHER or not at all — absent when
 * the valuation is unknown (unknown ≠ zero), and never `null`: a consumer
 * must not learn a different spelling from an event than from a response
 * (D-123 ruling 10's one free clause). */
function moneyPairRidesTogether(
  body: { value_at_risk_minor?: number | undefined; currency?: string | undefined },
  ctx: z.RefinementCtx,
): void {
  if ((body.value_at_risk_minor === undefined) !== (body.currency === undefined)) {
    ctx.addIssue({
      code: "custom",
      path: ["value_at_risk_minor"],
      message:
        "the money pair rides together: value_at_risk_minor and currency are both present or both absent (spec 07 §9.10, D-128)",
    });
  }
}

/** spec 07 §6 — `quality.inspection_created`. Consumers: 12 (notify assigned
 * qc). The "04 (step gate registration)" consumer is WITHDRAWN (D-123 ruling
 * 4): registration happens in module 04's own completion transaction through
 * `QC_GATE_PORT`, before this event exists, and a guard may not arrive over an
 * unordered at-least-once relay. */
export const QualityInspectionCreatedPayload = z.object({
  inspection_id: Uuid,
  inspection_no: z.string(),
  control_point_code: z.string(),
  stage: z.string(),
  origin: z.string(),
  blocking: z.boolean(),
  facility_id: Uuid,
  stock_unit_id: Uuid.nullable(),
  lot_id: Uuid.nullable(),
  work_order_id: Uuid.nullable(),
  work_order_operation_id: Uuid.nullable(),
  product_id: Uuid.nullable(),
  material_id: Uuid.nullable(),
  work_center_id: Uuid.nullable(),
});

/** spec 07 §6 — `quality.inspection_passed`. Consumers: 06 (release held
 * receiving stock per §3.3), 10, 09, 04 (PROJECTION ONLY — the SSE
 * station-queue push and the office badge; the flag itself was already cleared
 * in this module's transaction through the in-txn seam, §3.2/§3.4), 12.
 *
 * `is_retest` is §6's own definition: `retest_of_inspection_id IS NOT NULL`.
 * Carried as a derived boolean because a consumer ranking retests should not
 * have to know this module's column, and §2.12 defines the FPY population the
 * same way. */
export const QualityInspectionPassedPayload = z.object({
  inspection_id: Uuid,
  inspection_no: z.string(),
  control_point_code: z.string(),
  stage: z.string(),
  origin: z.string(),
  is_retest: z.boolean(),
  facility_id: Uuid,
  stock_unit_id: Uuid.nullable(),
  lot_id: Uuid.nullable(),
  work_order_id: Uuid.nullable(),
  work_order_operation_id: Uuid.nullable(),
  product_id: Uuid.nullable(),
});

/** spec 07 §6 — `quality.inspection_failed`. Consumers: 04 (PROJECTION ONLY,
 * same amendment — the flag was SET in this module's transaction; the event
 * drives the queue push and read models, which are commutative and idempotent
 * and therefore safe on this relay), 12 (the money-ranked exception feed), 10
 * (via `quality.receiving_failed`, which is 07-D's).
 *
 * `measured_summary` is the measured items that failed, as `{check item,
 * value, bounds}` — §6's "measured summary". Exact decimal text throughout
 * (D-041).
 *
 * `hold_id`, `value_at_risk_minor` and `currency` are §6's three 07-B fields,
 * OPTIONAL from 07-B-2 on — ABSENT rather than null, because §7's redaction
 * rule for money is absence and a consumer must not learn a different spelling
 * from an event than from a response (D-123 ruling 10's one free clause).
 * `hold_id` is absent when no hold was placed (§3.4's D-129 skip: an
 * unquarantinable subject completes with no hold), and the money pair is
 * absent when the valuation is unknown (§9.10) — it is NOT role-redacted
 * here: an outbox event has no caller to redact for, so consumers apply §7 at
 * their own serialization edge (module 05's shape; D-129). */
export const QualityInspectionFailedPayload = QualityInspectionPassedPayload.extend({
  failure_cause_code: z.string().nullable(),
  measured_summary: z.array(
    z.object({
      check_item_id: Uuid,
      name: z.string(),
      measured_value: z.string().nullable(),
      min_value: z.string().nullable(),
      max_value: z.string().nullable(),
    }),
  ),
  produced_shift_instance_id: Uuid.nullable(),
  detected_shift_instance_id: Uuid.nullable(),
  hold_id: Uuid.optional(),
  value_at_risk_minor: MinorMoney.optional(),
  currency: Currency.optional(),
}).superRefine(moneyPairRidesTogether);

/** spec 07 §6 — `quality.hold_placed` (slice 07-B-1). Consumers: 06 (status
 * flip — the synchronous in-txn call is primary; the event is for read
 * models), 09 (unallocate), 12. The money pair is OPTIONAL and travels
 * TOGETHER or not at all: absent when the valuation is unknown (§9.10 —
 * unknown ≠ zero), and never role-redacted here because an outbox event has no
 * caller to redact FOR — consumers apply §7 at THEIR serialization edge, the
 * rule module 05's ledger events follow. `origin` is D-129's §2.7 door:
 * "auto" — §3.4's auto_hold_on_fail; "manual" — POST /v1/quality/holds. */
export const QualityHoldPlacedPayload = z
  .object({
    hold_id: Uuid,
    hold_no: z.string(),
    facility_id: Uuid,
    stock_unit_id: Uuid.nullable(),
    lot_id: Uuid.nullable(),
    inspection_id: Uuid.nullable(),
    origin: z.enum(["auto", "manual"]),
    reason: z.string(),
    value_at_risk_minor: MinorMoney.optional(),
    currency: Currency.optional(),
  })
  .superRefine(moneyPairRidesTogether);

/** spec 07 §6 — `quality.hold_released` (slice 07-B-2, transition T2).
 * Consumers: 06, 09, 04 (projection only — the clear happened in this
 * module's transaction), 12. The payload is §6's row verbatim: `hold_id` +
 * `disposition_notes`, and the notes are a plain string because §3.5c makes
 * the release justification MANDATORY — a released hold without one is
 * unrepresentable (the 0036 CHECK). */
export const QualityHoldReleasedPayload = z.object({
  hold_id: Uuid,
  disposition_notes: z.string(),
});

/** spec 07 §6 — `quality.hold_disposed` (slice 07-B-2, transitions T3–T6).
 * Consumers: 06, 05, 10 (return_to_supplier), 04 (projection only — the
 * rework re-open and the hold clear are both in-txn calls into module-04-owned
 * services, §3.5a), 12.
 *
 * §6's "target refs", one per disposition kind, null where the kind produces
 * none: `rework_operation_id` — the RE-OPENED operation (never a spawned one,
 * D-123 ruling 2); `waste_event_id` — module 05's ledger row (§3.5b);
 * `return_id` — module 10's supplier return, null until module 10 exists (the
 * published-nullable pattern §0.1 rules); and §6's "downgrade info" rides
 * `disposition_notes` — the §3.5c-mandatory downgrade note is the only
 * downgrade fact 07-B produces (no module-06 re-grade ref exists to carry).
 *
 * `disposition` excludes `release` by construction: §3.5c's table sends
 * `release` to `released` (→ `quality.hold_released`) and only the other four
 * to `disposed`. */
export const QualityHoldDisposedPayload = z.object({
  hold_id: Uuid,
  disposition: z.enum(["rework", "scrap", "downgrade", "return_to_supplier"]),
  rework_operation_id: Uuid.nullable(),
  waste_event_id: Uuid.nullable(),
  return_id: Uuid.nullable(),
  disposition_notes: z.string().nullable(),
});

/** spec 07 §6 — `quality.control_point_versioned`. Consumers: audit/read
 * models. `superseded_control_point_id` is the row that was superseded — the
 * audit question "which definition did the inspections before this one execute
 * against" has exactly one answer, and it is this id. */
export const QualityControlPointVersionedPayload = z.object({
  control_point_id: Uuid,
  code: z.string(),
  version: z.number().int(),
  superseded_control_point_id: Uuid,
});

/**
 * spec 07 §6 — `quality.cut_completed` (slice 07-C, §3.5d step 9's outbox
 * append). Consumers: **06 alone today** (read model / genealogy views).
 *
 * **05, 09 and 12 ARE STATED RATHER THAN LISTED AS LIVE CONSUMERS (D-175).**
 * Module 05 learns the trim from `inventory.unit_cut` and this event is a
 * NO-OP for it today — it is kept in the catalog as the projection door a
 * later analytics need will use, because declaring a subscription that
 * nothing implements fails the live-registry parity gate in exactly the
 * direction that gate exists for. 09 and 12 do not exist (§0.1).
 *
 * **THERE IS NO `waste_event_id` ON THE SEGMENT SHAPE (D-157).** §2.9's column
 * was to be back-filled from module 05's trim-waste ledger row, and §5's
 * execute row promised it synchronously — neither is possible: module 05 mints
 * that row in a POST-COMMIT SQS consumer off `inventory.unit_cut`, so it does
 * not exist when this payload is built. `inventory_cut_event_id` is the
 * deterministic join key that replaces it, and the derivation
 * (`waste_events.client_event_id = derivedUuid(
 * "waste:unit_cut:{inventory_cut_event_id}:trim:{seq}")`) is stated in §2.9
 * for readers and analytics — never a query this module runs, because module
 * 07 reads no waste table (§6's ownership claim).
 *
 * `genealogy_depth` is the SOURCE's depth at propose time (original = 0,
 * D-158), carried so a consumer building a genealogy view never has to walk
 * the chain itself to learn how deep this cut was.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * REGISTERED IN `qualityProducerEvents` BELOW **BY 07-C-2, WITH ITS EMITTER**
 * — which is this file's own rule rather than a formality. 07-C-1 adjudicated
 * the SHAPE here, where the §6 row and D-157/D-158 were read, and deliberately
 * left the name out of the map, because `test/unit/module-catalog-parity.test
 * .ts` holds that map against the string literals in
 * `modules/quality/quality-events.ts` in BOTH directions: "an emitter for an
 * event no code path can raise is the same defect as a route that 501s (D-026,
 * D-033), so they join WITH their emitters", and declaring this one early
 * would have been the `inventory.level_drift_detected` defect that gate was
 * built for. 07-C-2 ships `QualityEvents.cutCompleted` (§3.5d step 9) and
 * moves the gate's declared count from 7 to 8 in the same change.
 * ─────────────────────────────────────────────────────────────────────────
 */
export const QualityCutCompletedPayload = z.object({
  cut_event_id: Uuid,
  inspection_id: Uuid,
  source_stock_unit_id: Uuid,
  /** Exact decimal text at module 06's `numeric(18,6)` scale (D-156, D-041). */
  source_length_m: z.string(),
  /** Exact decimal text at `numeric(12,3)` — the operator's metre mark. */
  fault_position_m: z.string(),
  genealogy_depth: z.number().int(),
  /** Module 06's `inventory.unit_cut` outbox event id (D-157). */
  inventory_cut_event_id: Uuid,
  segments: z.array(
    z.object({
      seq: z.number().int(),
      kind: z.enum(["stock", "waste"]),
      length_m: z.string(),
      new_stock_unit_id: Uuid.nullable(),
      retest_inspection_id: Uuid.nullable(),
    }),
  ),
});

// ═══════════════════════════════════════════════════════════════════════════
//  07-D's TWO RECEIVING EVENTS — SHAPES BY 07-D-2, NAMES + EMITTERS BY 07-D-4b
// ═══════════════════════════════════════════════════════════════════════════
// Both payloads below were adjudicated by slice 07-D-2 (schema + contracts),
// where §6's two amended rows and D-198 were read, and NEITHER NAME WAS IN
// `qualityProducerEvents` then — the ruling rather than an oversight: D-198
// says in its own title that they "land WITH their emitters", and
// `apps/api/test/unit/module-catalog-parity.test.ts` holds that map against the
// string literals in `modules/quality/quality-events.ts` in BOTH DIRECTIONS.
// Registering a name that deployment could not yet raise would have been the
// `inventory.level_drift_detected` defect that gate was built for, and the red
// would have landed on a sub-step that had not done the work.
//
// THAT WAS EXACTLY THE 07-C SPLIT, ONE SLICE LATER, and 07-D-4b is its second
// half: it ships §3.4's `receiving_failed` emit on a failed receiving
// completion and §3.5c's `receiving_line_dispositioned` emit on a
// `sampled_only`/`whole_lot` scope decision, registers both names below, and
// moves the parity gate's declared count 8 → 10 in the same commit.
//
// BOTH EMITS ARE GATED ON `sample_cohort_id`, and the gate is a statement about
// what these events MEAN rather than a defensive null-check. Their subject key
// is a SAMPLE (D-198: `lot_id` alone is a superset of the delivery that
// failed), and the cohort is minted by `onReceiptPosted` alone — so a
// receiving-stage inspection a qc lead created BY HAND against a delivery
// carries none, is not a sample, and raises neither event. It still fails, still
// auto-holds, and its hold still takes §3.5c's scope decision, which is why the
// scope surface deliberately does NOT require a cohort: refusing the operator a
// cohort-grain decision because no sampler happened to run would be a live dead
// control (D-118/D-167). Absence of the event is the honest answer; a nullable
// `sample_cohort_id` would instead teach every future consumer to handle a key
// that names nothing.
//
// The receiving surface's shapes were UNFILLABLE as §6 first wrote them, which
// is why both rows were re-keyed by D-198 before anything was built:
// `receiving_failed` was keyed on `receipt_line_id` (module 10's table, NULL on
// every row this deployment can write) and carried `supplier_id`, which had no
// writer at all until D-192 stamps it from `lots.supplier_id`. Three of five
// subject fields named nothing. `lot_id` and `sample_cohort_id` are the keys
// that exist.

/**
 * spec 07 §6 — `quality.receiving_failed` (slice 07-D, §3.4's failed-completion
 * side effect on a receiving-stage inspection).
 *
 * **NO CONSUMER IS DECLARED, and that is a ruling (D-198).** Module 10 — the
 * supplier scorecard and the receipt-line QC flag — does not exist (§0.1), and
 * D-175 already ruled this exact shape for `quality.cut_completed`: declaring a
 * subscription nothing implements fails the live-registry parity gate in
 * exactly the direction the gate exists for. §6's consumer column is a
 * statement of who WILL consume when they exist, which is what a catalog is
 * for.
 *
 * **THE COHORT KEY IS THE POINT.** `lot_id` alone is a SUPERSET of the delivery
 * that failed — module 06's adoption lets one lot span several receipts — so a
 * scorecard keyed on it would attribute the wrong quantity to the wrong
 * delivery on the first repeat purchase. `sample_cohort_id` is the only key
 * that names *this* receipt's sample (D-192, D-203: one cohort per matched
 * control point).
 *
 * **EMISSION IS PINNED IN THE INTEGRATION SUITE, never in a walkthrough.** A
 * walkthrough speaks HTTP and cannot read an outbox row (§0.1's module-12
 * rule), so the assertion that this payload carries the supplier, the material,
 * the lot, the cohort and the failure cause lives beside the emitter — which is
 * a strictly stronger statement than the "scorecard within 60 s" clause it
 * replaces in §10.1, one no code in this deployment could have met or missed.
 */
export const QualityReceivingFailedPayload = z
  .object({
    inspection_id: Uuid,
    /** The cohort's lot. Always present: D-192 stamps `lot_id` alongside
     * `stock_unit_id` on every receiving inspection precisely so §3.3's
     * arithmetic has a subject, and §2.5's subject CHECK is
     * `num_nonnulls(...) >= 1` — the two refs ride together and always have. */
    lot_id: Uuid,
    /** THIS receipt's sample, per matched control point (D-203). */
    sample_cohort_id: Uuid,
    /**
     * NULLABLE, and the null is a real state this repo's own demo data
     * contains. `supplier_id` gets its FIRST WRITER in this slice (D-192,
     * stamped from `lots.supplier_id`), and `LotsService.createInTxn` refuses a
     * purchased lot without one — but that rule is SERVICE-enforced and the
     * seed factory writes `lots` by raw SQL: `CU-2601`, the purchased copper
     * rod at the root of the M06-S08/S09 recall chain, is seeded
     * `origin='purchased'` with `supplier_id = NULL` deliberately, because
     * module 10 owns suppliers and a uuid would be a reference into a module
     * that never issued it (spec 06 §2.2's standing counterexample). Any
     * consumer grouping by supplier must tolerate the absence.
     */
    supplier_id: Uuid.nullable(),
    /** The received material. Non-nullable: a receiving binding carrying
     * `supplier_id` is refused 422 by this module's own catalog service and the
     * DB trigger forbids `product_id` on one, so EVERY receiving binding this
     * deployment can hold has exactly `material_id` set (D-187) — the match key
     * that produced this inspection is the value in this field. */
    material_id: Uuid,
    /**
     * PUBLISHED NULLABLE AND NULL (D-198), the pattern `return_id` already uses
     * on `hold_disposed`. Module 10 will fill it, and a consumer written
     * against the P2 shape should not have to learn a new field then.
     */
    receipt_line_id: Uuid.nullable(),
    /**
     * NON-NULLABLE, deliberately diverging from `inspection_failed`'s nullable
     * spelling of the same field. This event fires only from §3.4's FAILED
     * completion, and `qc_inspections_failure_cause_grain` makes a failed
     * inspection without a cause unrepresentable — so a nullable here would
     * declare a branch no code path can produce, and teach a consumer to write
     * one. The sibling's looser spelling is a shipped fact this cannot change;
     * copying it into a new event would be copying looseness on purpose.
     */
    failure_cause_code: z.string(),
    /** Exact decimal text at `numeric(14,3)` — the inspection's own
     * `quantity_inspected` scale (D-041: never a JSON number, because a
     * factory's money follows from this quantity). */
    quantity_rejected: z.string(),
    /** The money pair rides together or not at all, and is ABSENT (never null)
     * when the valuation is unknown — §9.10's unknown ≠ zero. NOT role-redacted
     * here: an outbox event has no caller to redact for, so consumers apply §7
     * at their own serialization edge (module 05's shape; D-129). */
    value_at_risk_minor: MinorMoney.optional(),
    currency: Currency.optional(),
  })
  .superRefine(moneyPairRidesTogether);

/**
 * spec 07 §6 — `quality.receiving_line_dispositioned` (slice 07-D, §3.5c's
 * receiving scope escalation).
 *
 * **NO CONSUMER IS DECLARED (D-198), and here the reason is doubled.** Module
 * 10's half (rejected quantity on the supplier scorecard) has no module to land
 * in; module 06's half (the sibling status flips) is performed IN this module's
 * own disposition transaction through module-06-owned service arms, never asked
 * for over the relay — so there is nothing on this name to subscribe.
 *
 * **`whole_lot`, NOT `whole_line` (D-193).** §6 shipped the enum spelling
 * `whole_line` and exactly one of its three values was wrong. There is no
 * receipt line — `receipt_lines` is module 10's, `receipt_line_id` is
 * permanently NULL, and there is no `receipts` table at all — and a LOT is a
 * SUPERSET of a receipt, because adoption lets one lot take several deliveries.
 * The word "line" would have promised a narrower blast radius than the product
 * delivers.
 */
export const QualityReceivingLineDispositionedPayload = z.object({
  hold_id: Uuid,
  /** The cohort's lot — the subject the escalation actually acted on. */
  lot_id: Uuid,
  /** THIS receipt's sample (D-192/D-203); the key `receipt_line_id` cannot be. */
  sample_cohort_id: Uuid,
  /** Published nullable and null until module 10 (D-198), as on
   * `receiving_failed`. */
  receipt_line_id: Uuid.nullable(),
  /**
   * TWO VALUES, NOT THREE, and the narrowing is §3.5c's own sentence: *"Scopes
   * (1) and (2) emit `quality.receiving_line_dispositioned`."* `inspect_all`
   * resolves by minting one `origin='manual'` inspection per un-sampled unit
   * and releasing each unit through its own passing inspection — a fan-out of
   * `inspection_created`, with no cohort-level decision for this event to
   * carry. Declaring a value no emitter can produce is the same defect as
   * declaring a name no emitter can raise, one level down.
   *
   * **If the build finds a real reason to emit on `inspect_all`, widen it
   * HERE, with the §3.5c amendment in the same change** — never by loosening
   * the enum quietly, which is how an event's vocabulary stops describing what
   * anything sends.
   */
  scope: z.enum(["sampled_only", "whole_lot"]),
  /** The kind applied — all five of §3.5c's, `release` included: unlike
   * `hold_disposed` (whose row excludes `release` by construction, because
   * §3.5c sends it to `hold_released`) this event reports the SCOPE decision,
   * which is orthogonal to which terminal state the hold reached. */
  disposition: z.enum(["release", "rework", "scrap", "downgrade", "return_to_supplier"]),
  /**
   * The stock units this disposition actually released, and **EMPTY IS THE
   * NORMAL CASE for `sampled_only`** (D-202/D-193): the siblings were never
   * `quarantined` — `ReceiptsService` inserts every received unit `filled`,
   * unconditionally — so there is nothing to put back, and the release that
   * matters is the LOT's `qc_status`. The array is what was WRITTEN, so a
   * consumer counting units released never has to know which of the two fences
   * was in play.
   */
  released_stock_unit_ids: z.array(Uuid),
});

// ═══════════════════════════════════════════════════════════════════════════
//  07-E's TWO CoA EVENTS (§3.6 steps 4–5, §6's rows)
// ═══════════════════════════════════════════════════════════════════════════
// Registered WITH their emitter (`modules/quality/quality-events.ts`) in the
// same change, per this file's own rule. §6 names modules 09 (attach to the
// shipment) and 12 (customer Telegram push via 17) as consumers; neither
// exists, and neither subscription is declared (D-175/D-198 — the consumers
// column records the spec's routing, `qualityConsumedEventNames` records the
// deployment's).

export const QualityCoaIssuedPayload = z.object({
  coa_document_id: Uuid,
  coa_no: z.string(),
  revision: z.number().int(),
  lot_id: Uuid.nullable(),
  shipment_id: Uuid.nullable(),
  customer_id: Uuid.nullable(),
  language: Locale,
  /** The unauthenticated customer link (§9.11) — absolute, so module 12 can
   * forward it to a Telegram thread without knowing the tenant's origin. */
  public_url: z.string(),
});

export const QualityCoaRevokedPayload = z.object({
  coa_document_id: Uuid,
  coa_no: z.string(),
  revision: z.number().int(),
  reason: z.string(),
});

export const qualityProducerEvents = {
  "quality.inspection_created": {
    payload: QualityInspectionCreatedPayload,
    consumers: ["12"],
  },
  "quality.inspection_passed": {
    payload: QualityInspectionPassedPayload,
    consumers: ["06", "10", "09", "04", "12"],
  },
  "quality.inspection_failed": {
    payload: QualityInspectionFailedPayload,
    consumers: ["04", "12", "10"],
  },
  // 07-B — the three quarantine-lifecycle events, each with its emitter in the
  // same change (quality-events.ts).
  "quality.hold_placed": {
    payload: QualityHoldPlacedPayload,
    consumers: ["06", "09", "12"],
  },
  "quality.hold_released": {
    payload: QualityHoldReleasedPayload,
    consumers: ["06", "09", "04", "12"],
  },
  "quality.hold_disposed": {
    payload: QualityHoldDisposedPayload,
    consumers: ["06", "05", "10", "04", "12"],
  },
  // 07-C-2 — the guided cut's one event, registered WITH its emitter
  // (`modules/quality/quality-events.ts`'s `cutCompleted`) and not one commit
  // before it, for the reason the block above the payload gives.
  "quality.cut_completed": {
    payload: QualityCutCompletedPayload,
    consumers: ["06"],
  },
  // 07-D-4b — the two receiving rows, each registered WITH its emitter
  // (`modules/quality/quality-events.ts`'s `receivingFailed` /
  // `receivingLineDispositioned`) and not one commit before it, for the reason
  // the block above the payloads gives.
  //
  // NO CONSUMER ON EITHER, and D-198 rules it twice over: module 10 — the
  // supplier scorecard and the receipt-line QC flag — does not exist (§0.1),
  // and module 06's half of the disposition (the sibling flips, the lot
  // release, the lot rejection) is performed IN module 07's own disposition
  // transaction through module-06-owned service arms, never asked for over the
  // relay. D-175 already ruled that declaring a subscription nothing implements
  // fails the live-registry parity gate in exactly the direction it exists for.
  "quality.receiving_failed": {
    payload: QualityReceivingFailedPayload,
    consumers: [],
  },
  "quality.receiving_line_dispositioned": {
    payload: QualityReceivingLineDispositionedPayload,
    consumers: [],
  },
  "quality.control_point_versioned": {
    payload: QualityControlPointVersionedPayload,
    consumers: [],
  },
  // 07-E — the CoA lifecycle pair (§3.6 steps 4–5), emitters in the same
  // change; §6's consumer routing (09, 12) recorded above the payloads.
  "quality.coa_issued": {
    payload: QualityCoaIssuedPayload,
    consumers: ["09", "12"],
  },
  "quality.coa_revoked": {
    payload: QualityCoaRevokedPayload,
    consumers: ["09", "12"],
  },
} as const;

export type QualityEventName = keyof typeof qualityProducerEvents;

export const qualityProducerEventNames = Object.keys(qualityProducerEvents) as QualityEventName[];

/**
 * Events module 07 CONSUMES over the relay (spec 07 §6 "Consumed by this
 * module"). EMPTY through 07-D, deliberately; 07-E adds the projection
 * refresher's two (see the constant below).
 *
 * §6 names four — `procurement.receipt_line_posted` (module 10 does not
 * exist; the receiving trigger fires from module 06's own receipt path and is
 * 07-D's binding), `production.operation_completed` (the in-process inspection
 * BIRTH happens synchronously inside module 04's completion transaction
 * through `QC_GATE_PORT` — D-123 ruling 4; the subscription §6 reserves is the
 * projection half, unbuilt), `sales.shipment_allocation_requested` (module 09
 * does not exist), and `inventory.unit_remeasured` (updates `source_length_m`
 * on proposed cuts — `qc_cut_events` is 07-C's table). Declaring a
 * subscription this deployment does not make would fail the live-registry
 * parity gate in both directions, which is exactly what it is for (the
 * module-04 precedent, restated).
 *
 * STILL EMPTY THROUGH 07-C-2, AND `inventory.unit_remeasured` IS THE ONE THAT
 * NEEDED DECIDING RATHER THAN DEFERRING. 07-C creates `qc_cut_events`, so the
 * subscription became one this module COULD honestly make — and 07-C-2 does
 * not make it, deliberately, because a consumed-event name declared ahead of
 * its subscriber is the identical defect as a producer name declared ahead of
 * its emitter, pointed the other way. **No worker ships in 07-C-2, so no name
 * is declared.**
 *
 * The reason it is a choice and not an omission: the CORRECTNESS fence against
 * a stale `source_length_m` is SYNCHRONOUS and already built — execute re-reads
 * module 06's measured length under the source's row lock and answers `409
 * QUALITY_CUT_LENGTH_CHANGED` with `current_length_m` in the extensions (§9.3).
 * A subscriber updating proposed cuts would arrive over an at-least-once,
 * UNORDERED relay and could therefore only ever be an advisory projection —
 * D-065's own rule says a write whose correctness needs A-before-B may not use
 * that relay, and this one's correctness does not need it. D-166 additionally
 * makes the whole cut surface ONLINE-ONLY, so the screen re-reads the
 * inspection rather than waiting for a projection, and D-162's supersede rule
 * means the operator's next propose replaces the stale cut for free. The name
 * lands WITH a worker, if a later slice finds a reader that needs one.
 */
export const qualityConsumedEventNames = [
  // 07-E (D-216-B pt 1): the projection-refresh subscriber
  // (`modules/quality/analytics/fpy-refresh.subscriber.ts`) consumes the two
  // FPY-moving events THIS module emits — a self-subscription over the relay,
  // legal under D-065's rule because the action is commutative and idempotent
  // (a debounced refresh of a projection; the function absorbs redelivery and
  // ordering both). The name lands WITH the worker, per this constant's own
  // long-standing rule above.
  "quality.inspection_passed",
  "quality.inspection_failed",
] as const;
